"use client";
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ShoppingCart, Search } from "lucide-react";
import { IActionGuide } from "./interfaces";
import { GenerateDIYThroughStream } from "./actions";
import { ToastError } from "@/lib/sonnerUtils";
import Image from "next/image";
import DIYImage from "@/../public/images/diy.png";
import Link from 'next/link';
import { useKindeBrowserClient } from "@kinde-oss/kinde-auth-nextjs";
import LoginRedirect from "@/components/LoginRedirect";
import CartaTokens from '@/../public/images/tokens_sm.png';
import { GetTokenCost } from "@/lib/tokensHandlers";
import HttpStatus from "@/lib/HttpStatus";
import AdSterraIFrameBannerAuto from "@/components/adsterra-ui/adsterra-ifram-auto";
import { readStreamableValue } from "@ai-sdk/rsc";

function DIYWrapped() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [guide, setGuide] = useState<IActionGuide | null>(null);
  const [userLocation, setUserLocation] = useState("Australia"); // Default
  const { user, isLoading, isAuthenticated } = useKindeBrowserClient();
  const [ tokensCost, setTokensCost ] = useState<number>(0);

  useEffect(() => {
    async function getTokensCost() {
      try {
        const costResp = await GetTokenCost('DIYGEN01');
  
        if(costResp.status != HttpStatus.OK) {
          throw new Error(costResp.error!);
        }
        
        setTokensCost(costResp.data);
      } catch(error) {
        setTokensCost(0);
      }
    }

    getTokensCost();

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(async (pos) => {
        const { latitude, longitude } = pos.coords;
        
        try {
          // Using OpenStreetMap's free Nominatim API
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await res.json();
          
          // Extract city/suburb and country
          const city = data.address.city || data.address.town || data.address.suburb;
          const country = data.address.country;
          
          setUserLocation(`${city}, ${country}`);
        } catch (error) {
          console.error("Geocoding failed", error);
          setUserLocation(""); // Fallback
        }
      });
    }
  }, []);

  const generateGuide = async () => {
    if (!query) return;
    setLoading(true);
    try {
      // const res: IDIYResponse = await GenerateDIY(query, userLocation);

      const { data } = await GenerateDIYThroughStream(query, userLocation, user?.id);

      // Read the streamable value chunks directly
      for await (const delta of readStreamableValue(data)) {
        // console.log('Getting delta...');
        if (delta) {
          // delta is a perfectly parsed, partial JSON object!
          // No manual JSON.parse strings or broken braces to worry about.
          setGuide(delta as IActionGuide);
          // console.log('Delta: ', delta);
        } else {
          console.log('Delta is blank');
        }
      }
      
      // if (response.status != HttpStatus.OK) throw new Error(response.error!);
      // setGuide(response.data);
      
      // force user tokens balance component to refresh
      window.dispatchEvent(new Event('tokens-deducted'));

      setLoading(false);
      
    } catch(error) {
      // console.error(error as Error);
      ToastError((error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement, HTMLInputElement>) => {
    setQuery(e.target.value);

    if(guide) setGuide(null);
  }

  return (
    <>
      <div className="space-y-8"> 
        <div className='text-lg'>
          <Link className='underline' href={"/"}>Home</Link>
          <span className='pl-2 pr-2'>{">"}</span>
          <Link className='underline' href={"/dashboard"}>Dashbord</Link>
          <span className='pl-2 pr-2'>{"> Do-It-Yourself"}</span>
        </div>
        
        {/* <AdSterraBannerAuto position='top' /> */}
        <AdSterraIFrameBannerAuto position="top" />

        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
          Do-It-Yourself Assistant
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Tell me what do you want to do. Let that be installing an electric appliance, 
          making a wooden table, or preparing a dish, I'll explain to you how, give you 
          the list of required items and/or ingredients, ane even tell you where to buy 
          them from for best value.
        </p>
        {/* Search Header */}
        <div className="text-center space-y-4 w-full">
          <div className="flex flex-col gap-2 w-full mx-auto">
            <Input 
              className="bg-white"
              placeholder="e.g. Fix a leaky tap, Cook authentic Syrian Kibbeh..." 
              value={query}
              onChange={handleSearchChange}
            />
            <Button onClick={generateGuide} disabled={loading} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white transition duration-200">
              <div className="flex flex-row justify-center items-center gap-2">
                <div className="text-lg">
                  {loading ? (
                      <div className="flex justify-center items-center gap-2">
                        <Loader2 className="animate-spin" />
                        <div>
                          Generating DIY plan
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-center items-center gap-2">
                        <div>Plan It</div>
                        <div className="flex flex-row justify-center items-center gap-1 text-sm">
                          <span>{"("}</span><Image src={CartaTokens} width={20} height={20} alt="Carta AI Tokens" />
                          <span className="ml-1">{tokensCost} tokens</span><span>{")"}</span>
                        </div>
                      </div>
                   )}
                </div>
              </div>
            </Button>
          </div>
        </div>

        <div className="w-full border-2 border-gray-300 rounded-md 
          min-h-52 bg-indigo-200 p-4">
          {guide ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Main Instructions (Left 2 Columns) */}
              <div className="md:col-span-2 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex flex-col md:flex-row justify-between 
                      items-center gap-3">
                      {guide.title}
                      <span className="text-xs font-normal px-2 py-1 bg-indigo-100 text-indigo-700 rounded">
                        {guide.difficulty} • {guide.estimatedTime}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <p className="text-sm text-muted-foreground italic border-l-4 pl-4">
                      {guide.overview}
                    </p>
                    <div className="space-y-4">
                      {guide.steps && guide.steps.length > 0 ? guide.steps.map((step, i) => (
                        <div key={i} className="flex gap-4">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold">
                            {i + 1}
                          </div>
                          <div>
                            <h4 className="font-semibold">{step.title}</h4>
                            <p className="text-sm text-gray-600">{step.instruction}</p>
                          </div>
                        </div>
                      ))
                      : null
                      }
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Shopping & Suppliers (Right Column) */}
              <div className="space-y-4">
                <h3 className="font-bold flex items-center gap-2 text-indigo-600">
                  <ShoppingCart size={20} /> Required Materials
                </h3>
                {guide.suppliers && guide.suppliers.length > 0 ? guide.suppliers.map((sup, i) => (
                  <Card key={i} className="border-indigo-100 bg-indigo-100">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-md flex flex-col justify-between gap-2 mb-4">
                        {sup.supplierName}
                        <span className="text-[10px] uppercase tracking-wider italic
                          text-indigo-500">
                          {sup.totalValueRating}
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {sup.items && sup.items.length > 0 ? sup.items.map((item, j) => (
                        <div key={j} className="text-xs border-b border-indigo-50 pb-2 last:border-0">
                          <div className="flex justify-between font-medium">
                            <span>{item.name}</span>
                            <span>{item.approxPrice}</span>
                          </div>
                          <p className="text-gray-500 text-[10px] mt-1">{item.reason}</p>
                        </div>
                      )) : null}
                      <Button 
                        variant="outline" 
                        className="w-full text-xs min-h-8 h-auto mt-2
                          whitespace-normal break-words"
                        onClick={() => window.open(`https://www.google.com/search?q=${sup.shopLinkHint}&tbm=shop`)}
                      >
                        <Search className="mr-2" />
                        Find all at {sup.supplierName}
                      </Button>
                    </CardContent>
                  </Card>
                ))
                  : null
                }
              </div>
            </div>
          )
          : (
            <div className="flex flex-col gap-4 w-full h-52 justify-center items-center">
                <div>              
                  <Image src={DIYImage} width={60} alt="Do It Youself Contents go here..." />
                </div>
                <div className="text-gray-400 italic">
                  DIY results will appear here...
                </div>
            </div>
          )}
        </div>
      </div>

      {/* 2. THE OVERLAY (Only shows if not logged in) */}
      {(!isAuthenticated && !isLoading) && (
        <LoginRedirect message="to analyze this job description" />
      )}

      <AdSterraIFrameBannerAuto position="bottom" />
    </>
  );
}

export default DIYWrapped